import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import dynamic from 'next/dynamic';
import useTranslation from 'next-translate/useTranslation';

// Third-party
import { KeyedMutator } from 'swr';
import { ClassNameValue } from 'tailwind-merge';

// Hooks
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

// Utils
import { classNames } from '@/utils';
import { post as postRequest } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

// Types
import { MessageType } from '@/types/messageType';
import { SocialProfileType } from '@/types/socialProfileType';

// Endpoints
import { COMMENT_ADD } from '@/lib/routes/apiEndpoints';
// Libs
import { FACEBOOK } from '@/lib/socialProfile';

// Components
import { SizeMe } from '@/components/base';
import { DefaultAvatar as Avatar } from '@/components/base/Avatar';
import Icon from '@/components/base/Icon';
import Loader from '@/components/base/Loader';
import CommentInput from '@/components/composite/Comments/CommentInput';
import { ProfileItem } from '@/components/composite/SelectedProfiles/ProfilesList/ProfileItem';
const EmojiPicker = dynamic(() => import('@/components/base/EmojiPicker'));

interface CommentFormProps {
    className?: ClassNameValue;
    socialProfile?: SocialProfileType;
    mediaId?: string;
    mutateComments?: KeyedMutator<any[]>;
    submitOptions?: {
        endpoint?: string;
        params?: Record<string, string>;
    };
    comments?: { data: MessageType[] }[];
    variant?: 'default' | 'socialInbox';
    placeholder?: string;
    onSubmit?: (comment: MessageType) => void;
    disabled?: boolean;
    submitAction?: (message: any) => Promise<any>;
}

export const CommentForm = ({
    className,
    socialProfile,
    mediaId,
    mutateComments,
    submitOptions = { endpoint: COMMENT_ADD },
    comments,
    placeholder,
    variant = 'default',
    disabled = false,
    onSubmit,
    submitAction
}: CommentFormProps) => {
    const { t } = useTranslation();
    const { activeSocialProfile } = useActiveSocialProfile();
    const currentSocialProfile = socialProfile ?? activeSocialProfile;
    const [cursorPosition, setCursorPosition] = useState(0);
    const [mentions, setMentions] = useState([]);
    const {
        control,
        getValues,
        handleSubmit,
        resetField,
        setError,
        setValue,
        formState: { errors, isSubmitting, isValid, isSubmitted }
    } = useFormContext();

    const mergedReplyParams = submitOptions.params
        ? {
              SPID: currentSocialProfile?.id,
              ...submitOptions.params
          }
        : {
              SPID: currentSocialProfile?.id,
              mediaID: mediaId
          };

    const submit = async ({ message }) => {
        let messageFormatted = message;
        if (currentSocialProfile?.social_network_type === FACEBOOK) {
            mentions.forEach((mention) => {
                messageFormatted = messageFormatted.replace(
                    `@[${mention.id}][${mention.display}]`,
                    `@[${mention.id}]`
                );
            });
        } else {
            mentions.forEach((mention) => {
                messageFormatted = messageFormatted.replace(
                    `@[${mention.id}][${mention.display.substring(1)}]`,
                    `${mention.display}`
                );
            });
        }

        try {
            const { data: comment } = await postRequest(
                injectEndpointUrlParams(submitOptions.endpoint, mergedReplyParams),
                {
                    message: messageFormatted
                }
            );

            // Add comment + increment total comments
            mutateComments?.(
                (prevDataComments) =>
                    prevDataComments.map((page, index) => ({
                        ...page,
                        data:
                            index === 0
                                ? [{ ...comment, replies: [], replies_count: 0 }, ...page.data]
                                : page.data,
                        summary: {
                            ...page.summary,
                            total_count: (page.summary?.total_count || 0) + 1
                        }
                    })),
                false
            );

            onSubmit?.(comment);
            resetField('message');
        } catch (e) {
            if (e.status === 422 && e.response?.data?.errors) {
                const errorsFields = e.response.data.errors;
                if (errorsFields) {
                    Object.keys(errorsFields).forEach((field) => {
                        const error_type = errorsFields[field][0];
                        // Display error under each field (only one here, the message field)
                        setError(field as keyof typeof errors, {
                            type: 'manual',
                            message: t(`errors:error_${String(field)}.${error_type}`)
                        });
                    });
                }
            } else {
                setError('message', {
                    type: 'manual',
                    message: t('errors:error_message.default')
                });
            }
        }
    };

    const addEmojiInMessage = (emoji) => {
        if (disabled) return;

        const initialMessage = getValues('message');
        const newMessage = `${initialMessage.substring(0, cursorPosition)}${
            emoji.native
        }${initialMessage.substring(cursorPosition)}`;
        setValue('message', newMessage, { shouldValidate: true });
        setCursorPosition(cursorPosition + newMessage.length - initialMessage.length);
    };

    const renderSubmitButton = () => {
        if (isSubmitting) return <Loader className="size-5" />;

        if (variant === 'default') {
            return (
                <button
                    type="submit"
                    disabled={!isValid || disabled}
                    className={classNames('flex items-center text-xl', {
                        'text-icoGray-400': !isValid,
                        'text-icoBlue': isValid,
                        'cursor-not-allowed opacity-50': disabled
                    })}
                    title={t('commons:media_viewer.send')}>
                    <Icon icon="dm" className="flex items-center justify-center" />
                </button>
            );
        }
        return (
            <button
                type="submit"
                disabled={!isValid || disabled}
                data-testid="comment-form-submit-button"
                className={classNames(
                    'flex items-center justify-center rounded-full bg-icoBlue px-2 py-1 text-white',
                    { 'bg-icoBlue': !disabled, 'cursor-not-allowed bg-icoGray-400': disabled }
                )}>
                <Icon icon="arrow-top" className="flex size-5 items-center justify-center" />
            </button>
        );
    };

    return (
        <SizeMe>
            {({ size }) => (
                <>
                    <div className="-ml-3 w-[calc(100%+1.5rem)] md:-ml-2 md:w-[calc(100%+1rem)] lg:-ml-5 lg:w-[calc(100%+2.5rem)]" />
                    <div
                        className={classNames(
                            {
                                'fixed bottom-0 z-10 -ml-3 mb-5 rounded-md bg-white/80 px-5 py-4 shadow-icoXL backdrop-blur-sm dark:bg-icoDarkMode-deepBlack/80 md:-ml-2 lg:-ml-5':
                                    variant === 'default',
                                'rounded-lg border-[1px] p-4 dark:border-icoDarkMode-beton sm:gap-5':
                                    variant === 'socialInbox',
                                'border border-icoRed-400 pb-2':
                                    isSubmitted && errors?.message?.message
                            },
                            className
                        )}
                        style={{
                            width: variant === 'default' ? `${size.width}px` : 'unset'
                        }}>
                        <form
                            onSubmit={handleSubmit(submitAction ?? submit)}
                            className="flex items-center">
                            {variant === 'default' ? (
                                <Avatar
                                    url={currentSocialProfile?.external_api_data?.avatar}
                                    fallback={currentSocialProfile?.slug?.[0]}
                                    size="lg"
                                />
                            ) : (
                                <ProfileItem size="base" profile={currentSocialProfile} />
                            )}

                            <div className="relative flex-1 pr-2">
                                <Controller
                                    control={control}
                                    name="message"
                                    render={({ field: { onChange, value, ref } }) => (
                                        <CommentInput
                                            inputRef={ref}
                                            placeholder={placeholder}
                                            socialProfile={socialProfile}
                                            value={value}
                                            onChange={onChange}
                                            mentions={mentions}
                                            setMentions={setMentions}
                                            setCursorPosition={setCursorPosition}
                                            disabled={isSubmitting || disabled}
                                            comments={comments}
                                            submitAction={handleSubmit(submitAction ?? submit)}
                                        />
                                    )}
                                />
                            </div>
                            <EmojiPicker
                                selectEmoji={addEmojiInMessage}
                                className={classNames('mr-5 text-xl', {
                                    'text-black dark:text-white': variant === 'socialInbox'
                                })}
                                iconType={variant === 'default' ? 'engagement' : 'emoji-comment'}
                                disabled={disabled}
                            />
                            {renderSubmitButton()}
                        </form>
                        {isSubmitted && errors?.message && (
                            <p className="font-GMedium text-xs text-icoRed-400">
                                {typeof errors.message.message === 'string'
                                    ? errors.message.message
                                    : null}
                            </p>
                        )}
                    </div>
                </>
            )}
        </SizeMe>
    );
};
